'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Spinner,
  ScrollShadow,
  Select,
  SelectItem,
  Input,
} from '@heroui/react';
import { FilterOption } from './types';
import Typography from '../typography/Typography';
import { CloseIc, SearchInputIc } from '../icons';

interface ModelSearchSelectProps {
  placeholder: string;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  onSearchChange: (searchTerm: string) => void;
  options: FilterOption[];
  isLoading?: boolean;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'campaign';
  autoWidth?: boolean;
  // Additional filters for offers page
  showAdditionalFilters?: boolean;
  priceFilter?: string;
  onPriceFilterChange?: (value: string) => void;
  topCreatorsValue?: string;
  onTopCreatorsChange?: (value: string) => void;
  t?: (key: string) => string;
  // Custom search placeholder inside the dropdown
  searchPlaceholder?: string;
}

const ModelSearchSelect: React.FC<ModelSearchSelectProps> = ({
  placeholder,
  selectedKey,
  onSelectionChange,
  onSearchChange,
  options,
  isLoading = false,
  startContent,
  className = '',
  disabled = false,
  variant = 'default',
  autoWidth = false,
  showAdditionalFilters = false,
  priceFilter = '',
  onPriceFilterChange,
  topCreatorsValue = '',
  onTopCreatorsChange,
  t = (key: string) => key,
  searchPlaceholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedOption = options.find((option) => option.key === selectedKey);

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: 'bg-auth-surface border-none rounded-2 !px-3 !min-h-9 !h-9',
          value: 'text-sm !text-auth-muted',
        };
      default:
        return {
          trigger:
            'h-9 bg-gray-100 border-none rounded-2 !px-3 hover:bg-gray-200 focus:border-blue-500',
          value: 'text-sm !text-auth-muted',
        };
    }
  };

  const variantStyles = getVariantStyles();

  const handleOptionSelect = (option: FilterOption) => {
    onSelectionChange(option.key);
    setIsOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm('');
      onSearchChange('');
    }
  };

  return (
    <div className={autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'}>
      <Popover
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="bottom-start"
        classNames={{
          content: 'max-w-[21.25rem] w-[21.25rem] py-4  px-4',
        }}
      >
        <PopoverTrigger>
          <Button
            className={`${variantStyles.trigger} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'} ${className} justify-start`}
            disabled={disabled}
          >
            <div className="flex items-center gap-1 w-full">
              {selectedOption && selectedOption.key !== 'all' && selectedOption.avatar ? (
                <img
                  src={selectedOption.avatar}
                  className="w-5 h-5 rounded-1"
                  alt={selectedOption.name || ''}
                />
              ) : (
                startContent
              )}
              <span
                className={`${selectedOption && selectedOption.key !== 'all' ? 'text-sm !text-auth-ink' : variantStyles.value} truncate flex-1 text-left`}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="w-full flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <Typography variant="body2">
                {t('partner.revshare.offers.filters.account') || 'Аккаунт'}
              </Typography>
              <Input
                type="text"
                placeholder={
                  searchPlaceholder ||
                  t('partner.revshare.offers.filters.accountSearchPlaceholder') ||
                  'Search by model ID'
                }
                size="sm"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                endContent={<SearchInputIc />}
                classNames={{
                  input: 'text-sm',
                  inputWrapper:
                    '!min-h-9 !max-h-9 bg-transparent border border-auth-strength-none rounded-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
                }}
              />
            </div>
            <ScrollShadow
              className="max-h-[12.5rem] overflow-y-auto"
              aria-label="Навигационное меню"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Spinner size="sm" />
                </div>
              ) : options.length === 0 ? (
                <div className="text-sm text-gray-500">
                  {searchTerm
                    ? t('partner.revshare.offers.filters.modelsNotFound') || 'Модели не найдены'
                    : t('partner.revshare.offers.filters.startTyping') ||
                      'Начните вводить для поиска'}
                </div>
              ) : (
                <>
                  {options
                    .filter((option) => option.key !== 'all')
                    .map((option: any) => {
                      return (
                        <div
                          key={option.key}
                          className={`flex items-center justify-between gap-2 w-full text-left p-1 rounded-2 hover:bg-gray-100 cursor-pointer text-sm whitespace-normal break-words ${
                            selectedKey === option.key ? 'bg-[#EEEEEF] ' : ''
                          }`}
                          onClick={() => handleOptionSelect(option)}
                        >
                          <div className="flex items-center gap-2">
                            <img src={option?.avatar} className="w-6 h-6 rounded-[0.3125rem]" />
                            <span>{option.name}</span>
                            <a
                              target="__blank"
                              href={`https://onlyfans.com/${option.username}`}
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Typography variant="body2" className="!text-partner">
                                @{option.username}
                              </Typography>
                            </a>
                          </div>
                          {selectedKey === option.key && (
                            <Button
                              //@ts-ignore
                              size="xs"
                              className="h-5 rounded-2 !w-5 p-0 bg-transparent"
                              onPress={() =>
                                handleOptionSelect({
                                  key: 'all',
                                  label: t('partner.revshare.offers.filters.model') || 'Модель',
                                })
                              }
                            >
                              <CloseIc />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  {searchTerm && options.filter((option) => option.key !== 'all').length === 0 && (
                    <div className="text-sm text-gray-500 p-1">
                      {t('partner.revshare.offers.filters.modelsNotFound') || 'Модели не найдены'}
                    </div>
                  )}
                </>
              )}
            </ScrollShadow>

            {/* Additional filters for offers page */}
            {showAdditionalFilters && (
              <>
                {/* Price filter */}
                <div className="flex flex-col gap-2 pt-2 border-t border-tin">
                  <Typography variant="body2">
                    {t('partner.revshare.offers.filters.price') || 'Стоимость'}
                  </Typography>
                  <Select
                    size="sm"
                    selectedKeys={priceFilter ? [priceFilter] : []}
                    onSelectionChange={(keys) => {
                      const value = Array.from(keys)[0] as string;
                      onPriceFilterChange?.(value || '');
                    }}
                    placeholder={t('partner.revshare.offers.filters.priceAny') || 'Любая'}
                    classNames={{
                      trigger:
                        '!min-h-9 !h-9 bg-transparent border border-auth-strength-none rounded-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
                      value: 'text-sm',
                    }}
                  >
                    <SelectItem key="any">
                      {t('partner.revshare.offers.filters.priceAny') || 'Любая'}
                    </SelectItem>
                    <SelectItem key="free">FREE</SelectItem>
                    <SelectItem key="free trial">
                      {/* {t('partner.revshare.offers.filters.paid') || 'Free-trial'} */}
                      {t('partner.revshare.offers.filters.freeTrial') || 'Free-trial'}
                    </SelectItem>
                  </Select>
                </div>

                {/* Top creators filter */}
                <div className="flex flex-col gap-2 ">
                  <Typography variant="body2">
                    {t('partner.revshare.offers.filters.topCreators') || 'Топ создателей контента'}
                  </Typography>
                  <Input
                    type="number"
                    placeholder="<0"
                    size="sm"
                    value={topCreatorsValue}
                    onChange={(e) => onTopCreatorsChange?.(e.target.value)}
                    classNames={{
                      input: 'text-sm',
                      inputWrapper:
                        '!min-h-9 !max-h-9 bg-transparent border border-auth-strength-none rounded-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ModelSearchSelect;
